import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../../styles/scope3/Dashboard/purchaseGoods.module.css";
import BarChart from "../../../../Components/GraphNCharts/BarChart";
import PeriodFilter from "../../../../Components/Common/PeriodFilter";
import {
  formatDashboardEmissionValue,
  formatNumberScientificCompact,
  formatTruncatedDecimalPlaces,
} from "../../../../utils/common";
import useAuthStore from "../../../../store/ContextUser";
import { get } from "../../../../utils/api";

const USE_PHASE_YEAR_MIN = 2020;
const USE_PHASE_YEAR_MAX = 2026;

const normalizeProductBars = (list, valueKeys) => {
  if (!Array.isArray(list)) return { labels: [], dataPoints: [] };
  const labels = [];
  const dataPoints = [];
  for (const item of list) {
    const label =
      item?._id ??
      item?.productId ??
      item?.productName ??
      item?.name ??
      "";
    let value = 0;
    for (const key of valueKeys) {
      if (item != null && item[key] != null && item[key] !== "") {
        value = Number(item[key]);
        break;
      }
    }
    if (!Number.isFinite(value)) value = 0;
    labels.push(String(label));
    dataPoints.push(value);
  }
  return { labels, dataPoints };
};

const Dashboard = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const { selectedCompany } = useAuthStore();

  const [selectedFacility, setSelectedFacility] = useState([]);
  const [fromDate, setFromDate] = useState({
    month: 1,
    year: currentYear,
  });
  const [toDate, setToDate] = useState({
    month: currentDate.getMonth() + 1,
    year: currentYear,
  });

  const [totalLifetimeEmissionKg, setTotalLifetimeEmissionKg] = useState(0);
  const [energyPerProduct, setEnergyPerProduct] = useState({
    labels: [],
    dataPoints: [],
  });
  const [usePhasePerProduct, setUsePhasePerProduct] = useState({
    labels: [],
    dataPoints: [],
  });

  const [usePhaseYear, setUsePhaseYear] = useState(() =>
    Math.min(Math.max(currentYear, USE_PHASE_YEAR_MIN), USE_PHASE_YEAR_MAX),
  );

  const facilityQuery = selectedFacility.join(",");
  const companyId = selectedCompany?._id || "";

  const totalLifetimeUrl = useMemo(
    () =>
      `/dashboard/scope3/useofsoldproducts/totalLifTimeEmission?startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}&facilities=${facilityQuery}&companyId=${companyId}`,
    [fromDate, toDate, facilityQuery, companyId],
  );

  const usePhaseUrl = useMemo(
    () =>
      `/dashboard/scope3/useofsoldproducts/annualUseFacePerProduct?year=${usePhaseYear}&facilities=${facilityQuery}&companyId=${companyId}`,
    [usePhaseYear, facilityQuery, companyId],
  );

  const formatCardMetric = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return "0";
    if (n === 0) return "0";
    const abs = Math.abs(n);
    if (abs > 0 && abs < 0.1) {
      return formatNumberScientificCompact(n, 2);
    }
    return formatTruncatedDecimalPlaces(n, 3);
  };

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    const loadLifetime = async () => {
      try {
        const res = await get(totalLifetimeUrl);
        if (cancelled) return;
        const body = res?.data ?? {};
        setTotalLifetimeEmissionKg(Number(body.totalEmission ?? 0));
        const energyList =
          body.energyConsmptionPerProduct ??
          body.energyConsumptionPerProduct ??
          [];
        setEnergyPerProduct(
          normalizeProductBars(energyList, [
            "totalEnergyConsumptionPerProduct",
            "totalEnergyConsumption",
            "energyConsumption",
          ]),
        );
      } catch (e) {
        if (!cancelled) {
          console.error("totalLifTimeEmission", e);
          setTotalLifetimeEmissionKg(0);
          setEnergyPerProduct({ labels: [], dataPoints: [] });
        }
      }
    };

    loadLifetime();
    return () => {
      cancelled = true;
    };
  }, [totalLifetimeUrl, companyId]);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    const loadUsePhase = async () => {
      try {
        const res = await get(usePhaseUrl);
        if (cancelled) return;
        const body = res?.data ?? {};
        const list =
          body.annualUseFacePerProduct ??
          body.annualUsePhaseEmissionPerProduct ??
          body.usePhaseEmissionPerProduct ??
          body.data ??
          [];
        setUsePhasePerProduct(
          normalizeProductBars(list, [
            "totalEmission",
            "annualUsePhaseEmissionPerProduct",
            "annualEmissionPerProduct",
            "usePhaseEmission",
            "totalEmissionPerProduct",
            "emission",
            "annualEmission",
          ]),
        );
      } catch (e) {
        if (!cancelled) {
          console.error("usePhaseEmissionPerProduct", e);
          setUsePhasePerProduct({ labels: [], dataPoints: [] });
        }
      }
    };

    loadUsePhase();
    return () => {
      cancelled = true;
    };
  }, [usePhaseUrl, companyId]);

  const generateYearList = (startYear = 2020) => {
    const years = [];
    for (let year = startYear; year <= currentYear; year++) years.push(year);
    return years;
  };

  const yearList = generateYearList(2020);

  const usePhaseYearOptions = useMemo(() => {
    const years = [];
    for (let y = USE_PHASE_YEAR_MIN; y <= USE_PHASE_YEAR_MAX; y++) years.push(y);
    return years;
  }, []);

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>Use of Sold Products</p>
        <PeriodFilter
          {...{
            fromDate,
            setFromDate,
            toDate,
            setToDate,
            yearList,
            setSelectedFacility,
            selectedFacility,
          }}
        />
      </div>

      <div className={styles.purchase_container}>
        <div
          className={styles.purchase_container}
          style={{ paddingTop: "0.5rem" }}
        >
          <div className={styles.card_container} style={{ width: "100%" }}>
            <p className={styles.graph_heading}>
              Total Lifetime Emission (kg CO₂e)
            </p>
            <p className={styles.graph_value}>
              {formatCardMetric(totalLifetimeEmissionKg)}
            </p>
          </div>
        </div>

        <div
          className={styles.chart_container}
          style={{ width: "calc(100% - 0.6rem)" }}
        >
          <div className={styles.graph_container_header}>
            <p
              className={`${styles.graph_heading} ${styles.graph_heading_bar_ref}`}
            >
              Annual Use-Phase Emissions (per product)
            </p>
            <select
              aria-label="Year for use-phase emissions"
              name="usePhaseYear"
              value={usePhaseYear}
              onChange={(e) => setUsePhaseYear(Number(e.target.value))}
              className={styles.heading_select_year}
            >
              {usePhaseYearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <BarChart
            labels={usePhasePerProduct.labels}
            datasets={[
              {
                label: "Use-phase emissions",
                data: usePhasePerProduct.dataPoints,
                backgroundColor: "rgba(59, 130, 246, 0.95)",
                borderColor: "rgba(59, 130, 246, 1)",
                borderWidth: 0,
              },
            ]}
            customHeight="260px"
            ellipsis={false}
            linearTickFormatter={formatDashboardEmissionValue}
            horizontalCleanStyle
            valueAxisTitle="tCO₂e"
          />
        </div>

        <div
          className={styles.chart_container}
          style={{ width: "calc(100% - 0.6rem)" }}
        >
          <div className={styles.graph_container_header}>
            <p
              className={`${styles.graph_heading} ${styles.graph_heading_bar_ref}`}
            >
              Energy consumption (Lifetime per product)
            </p>
          </div>
          <BarChart
            labels={energyPerProduct.labels}
            datasets={[
              {
                label: "Energy",
                data: energyPerProduct.dataPoints,
                backgroundColor: "rgba(59, 130, 246, 0.95)",
                borderColor: "rgba(59, 130, 246, 1)",
                borderWidth: 0,
              },
            ]}
            customHeight="260px"
            ellipsis={false}
            linearTickFormatter={formatDashboardEmissionValue}
            horizontalCleanStyle
            valueAxisTitle="(MJ)"
          />
        </div>
      </div>
    </>
  );
};

export default Dashboard;
